import type { ApiErrorBody } from '@shared/types';

// Every call to the Clientbook server goes through here: JSON in and out, readable errors,
// and the app header the server requires on every change.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Per-field messages for forms, keyed by field name. */
  readonly fields: Record<string, string>;
  readonly details: Record<string, unknown>;

  constructor(status: number, message: string, code: string, details?: ApiErrorBody['error']['details']) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = details?.fields ?? {};
    this.details = (details ?? {}) as Record<string, unknown>;
  }
}

type Listener = (err: ApiError) => void;
const signedOutListeners = new Set<Listener>();

/** Called when the server says the session has ended, so the app can return to the sign-in screen. */
export function onSignedOut(fn: Listener): () => void {
  signedOutListeners.add(fn);
  return () => signedOutListeners.delete(fn);
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** Query parameters; empty values are left out. */
  query?: Record<string, string | number | boolean | null | undefined | (string | number)[]>;
}

function withQuery(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) for (const v of value) params.append(key, String(v));
    else params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const url = withQuery(`/api${path}`, opts.query);
  const headers: Record<string, string> = { Accept: 'application/json', 'x-clientbook': '1' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: payload, signal: opts.signal, credentials: 'same-origin' });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'Clientbook cannot reach the server. Check your connection and try again.', 'offline');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const e = (data as ApiErrorBody | null)?.error;
    const err = new ApiError(
      res.status,
      e?.message ?? (res.status >= 500 ? 'Something went wrong on the server. Try again in a moment.' : `The request failed (${res.status}).`),
      e?.code ?? 'error',
      e?.details,
    );
    if (res.status === 401 && !path.startsWith('/auth/login')) for (const fn of signedOutListeners) fn(err);
    throw err;
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body ?? {}, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body ?? {}, opts),
  del: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, body, opts),
};

/** A readable message for anything thrown by a request or a mutation. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Try again.';
}
